import assignIn from 'lodash/assignIn';
import isNaN from 'lodash/isNaN';
import toPairs from 'lodash/toPairs';
import { scaleLinear, type ScaleLinear } from 'd3-scale';
import { curveMonotoneX, line as d3Line } from 'd3-shape';
import { max } from 'd3-array';
import { d3, wordWrap } from './d3';
import { NORMAL_FONT_SIZE, MEDIUM_FONT_SIZE } from './utils';
import type { ScalesResultT } from './spider';
import type { ChoicesResultT } from './types';

const LAYOUT = {
  width: 1360,
  height: 540,
};
const MARGIN = {
  top: LAYOUT.height * 0.12,
  right: LAYOUT.width * 0.25,
  bottom: LAYOUT.height * 0.12,
  left: LAYOUT.width * 0.05,
};
const LEGEND_WIDTH = 300;
const LEGEND_MARGIN = 50;
const LEGEND_TEXT_WIDTH = LEGEND_WIDTH - LEGEND_MARGIN;
const MIN_TEXT_BOX_HEIGHT = 30;
const MAX_TEXT_BOX_HEIGHT = 40;
const CHOICES_THRESHOLD = 6;
const LEGEND_CIRCLE_RADIUS = 8;
const LEGEND_TEXT_MARGIN = 15;
const TICK_PADDING = 20;
const HIDDEN_LINE_OPACITY = 0.35;
const HIGHLIGHT_STROKE_WIDTH = 5;
const DATA_POINTS_BREAKPOINT = 5;
const STROKE_WIDTH = 2;

type Score = ScalesResultT['score'] | ChoicesResultT['score'];

interface Value {
  index: number;
  score: number;
}
interface Option {
  label: string;
  id: number;
  values: Value[];
}

interface ResponseResult {
  title: string;
  responses: Array<number>;
}
interface ResponseSession {
  session: number;
  participantCount: number;
  responses: Array<ResponseResult>;
}

export interface TrendResultT {
  id: number;
  current: boolean;
  label: string;
  data: ResponseSession | undefined;
}

interface QuestionChoice {
  id: number;
  label: string;
  position: number;
  correct_answer: boolean;
}
interface T {
  selector: string;
  results: TrendResultT[];
  choices: QuestionChoice[];
  colors: string[];
  textColor: string;
  type: 'scales' | 'choices';
  hoverId: number | null;
  setHoverId: (id: number | null) => void;
}

/**
 * Trends: line chart to view multiple choice or scales data over multiple sessions
 */
export class Trends implements T {
  readonly selector: string;
  readonly results: TrendResultT[];
  readonly choices: QuestionChoice[];
  readonly colors: string[];
  readonly textColor: string;
  readonly type: 'scales' | 'choices';
  readonly hoverId: number | null;
  readonly setHoverId: (id: number | null) => void;
  options: Option[] = [];
  x: ScaleLinear<number, number> = scaleLinear();
  y: ScaleLinear<number, number> = scaleLinear();

  /**
   * @param selector: string, selection of a dom node, e.g., #trends-${questionId}
   * @param results: result data by session
   * @param colors: array of color strings, e.g., ['blue', 'green', 'red']
   * @param textColor: color string, e.g., '#e8e8e8'
   * @param type: string, specifies result type for correct data aggregation (i.e., 'scales' or 'choices')
   * @param hoverId: number, references an option that should be highlighted
   * @param setHoverId: setter for highlighting an option
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.results = props.results;
    this.colors = props.colors;
    this.textColor = props.textColor;
    this.type = props.type;
    this.hoverId = props.hoverId;
    this.setHoverId = props.setHoverId;
    this.choices = props.choices;
  }

  /**
   * Calculate the score for an option
   *
   * @param score: list of numbers
   * @returns number, score
   */
  private getScore = (score: Score): number => {
    if (this.type === 'scales') {
      const respondents = Object.values(score).reduce((m, s) => m + s, 0);
      const scoreSum = toPairs(score).reduce(
        (m, s) => m + parseInt(s[0], 10) * s[1],
        0,
      );
      const mean = scoreSum / respondents;
      return isNaN(mean) ? 0 : Math.round(mean * 10) / 10;
    }
    return score[0] || 0;
  };

  /**
   * Adjusts scales and initializes x and y axes
   */
  private updateAxes = (): void => {
    const container = d3
      .select(this.selector)
      .attr('tranform', `translate(${MARGIN.left}, ${MARGIN.top})`);
    const xAxisGroup = container
      .selectAll('.x-axis-group')
      .data(['']) // Only append one
      .join('g')
      .attr('class', 'x-axis-group');
    const yAxisGroup = container
      .selectAll('.y-axis-group')
      .data([''])
      .join('g')
      .attr('class', 'y-axis-group');
    const width =
      LAYOUT.width - MARGIN.left - MARGIN.right - LEGEND_WIDTH - LEGEND_MARGIN;
    const height = LAYOUT.height - MARGIN.top - MARGIN.bottom;
    this.x.range([0, width]);
    this.y.range([height, 0]);
    const xAxis = d3.axisBottom<number>(this.x);
    const yAxis = d3
      .axisLeft(this.y)
      .tickSize(-width)
      .tickPadding(TICK_PADDING);

    if (this.results.length > DATA_POINTS_BREAKPOINT) {
      this.y.range([height - MARGIN.bottom, 0]);
    }

    const data = this.results.map((s, i) => {
      const results = s.data?.responses ?? [];
      const scoreMap = results.reduce(
        (m: Record<string, number>, r: ResponseResult) => {
          m[r.title] = this.getScore(r.responses);
          return m;
        },
        {},
      );
      return assignIn(
        {
          index: i,
          id: s.id,
          current: s.current ?? false,
          label: s.label,
        },
        scoreMap,
      );
    });
    this.options = this.choices.map((option, i) => ({
      label: option.label,
      id: option.id,
      values: data.map((d) => {
        return {
          index: d.index,
          score: d[option.label] ?? 0,
        };
      }),
    }));

    // Subtract 1 to get from get correct range of data points
    const dataPoints = data.length;
    this.x.domain([0, dataPoints - 1]);
    this.y.domain([
      0,
      max(this.options, (c) => max(c.values, (v) => v.score)) ?? 0,
    ]);

    const xLabels = data.reduce((a: Record<number, string>, d) => {
      a[d.index] = d.label;
      return a;
    }, {});

    xAxis
      .tickFormat((d: number) => xLabels[d] ?? '')
      .tickSize(0)
      .tickPadding(TICK_PADDING);

    // Append new axes
    xAxisGroup
      .attr(
        'transform',
        `translate(0,${
          dataPoints > DATA_POINTS_BREAKPOINT ? height - MARGIN.bottom : height
        })`,
      )
      .call(xAxis as any);
    yAxisGroup.call(yAxis as any);

    // Update axis properties
    xAxisGroup
      .selectAll('text')
      .style('text-anchor', () =>
        dataPoints > DATA_POINTS_BREAKPOINT ? 'end' : 'middle',
      )
      .attr('transform', () =>
        dataPoints > DATA_POINTS_BREAKPOINT ? 'rotate(-45)' : 'rotate(0)',
      )
      .attr('fill', this.textColor)
      .attr('font-size', MEDIUM_FONT_SIZE);
    yAxisGroup
      .selectAll('line')
      .attr('opacity', 0.5)
      .attr('stroke', this.textColor)
      .attr('stroke-dasharray', `${STROKE_WIDTH},${STROKE_WIDTH}`);
    yAxisGroup
      .selectAll('text')
      .attr('fill', this.textColor)
      .attr('font-size', MEDIUM_FONT_SIZE);
    yAxisGroup
      .selectAll('path')
      .attr('stroke', this.textColor)
      .style('stroke-width', STROKE_WIDTH);
  };

  /**
   * Creates trend lines
   */
  private updateLines = (): void => {
    const line = d3Line()
      .curve(curveMonotoneX)
      .x((d: any) => this.x(d.index))
      .y((d: any) => this.y(d.score));

    // Select
    const paths = d3
      .select(this.selector)
      .selectAll('.trends-line')
      .data(this.options);
    // Enter
    paths
      .enter()
      .append('path')
      .attr('class', 'trends-line')
      .attr('fill', 'none')
      .attr('stroke', (_, i) => this.colors[i % this.colors.length] ?? 'black')
      .attr('stroke-width', STROKE_WIDTH)
      .attr('d', (d: any) => line(d.values));
    // Update
    paths
      .attr('d', (d: any) => line(d.values))
      .attr('opacity', (d) =>
        this.hoverId === null || this.hoverId === d.id
          ? 1
          : HIDDEN_LINE_OPACITY,
      )
      .attr('stroke-width', (d) =>
        this.hoverId !== null && this.hoverId === d.id
          ? HIGHLIGHT_STROKE_WIDTH
          : STROKE_WIDTH,
      )
      .on('mouseover', (_event, d) => this.setHoverId(d.id))
      .on('mouseout', () => this.setHoverId(null));
    // Exit
    paths.exit().remove();
  };

  /**
   * Creates legend with labels, colored circles, and highlighting capability
   */
  private updateLegend = (): void => {
    const legendGroup = d3
      .select(this.selector)
      .selectAll('.legend-group')
      .data([''])
      .join('g')
      .attr('class', 'legend-group');
    const width =
      LAYOUT.width - MARGIN.left - MARGIN.right - LEGEND_WIDTH - LEGEND_MARGIN;
    const height = LAYOUT.height - MARGIN.top - MARGIN.bottom;
    const virtualTextBoxHeight =
      this.choices.length > CHOICES_THRESHOLD
        ? MIN_TEXT_BOX_HEIGHT
        : MAX_TEXT_BOX_HEIGHT;

    // Select
    const legendItems = legendGroup
      .attr(
        'transform',
        `translate(${width + LEGEND_MARGIN}, ${
          height / 2 - (this.choices.length * virtualTextBoxHeight) / 2
        })`,
      )
      .selectAll<SVGGElement, QuestionChoice>('.legend-item')
      .data(this.choices);
    // Enter
    const legendItemsEnter = legendItems
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (_, i) => `translate(0, ${i * virtualTextBoxHeight})`);
    const legendItemsCircle = legendItemsEnter
      .append('circle')
      .attr('r', LEGEND_CIRCLE_RADIUS)
      .attr('stroke', (_, i) => this.colors[i % this.colors.length] ?? 'black')
      .attr('stroke-width', 5)
      .attr('fill', (_, i) => this.colors[i % this.colors.length] ?? 'black')
      .attr('stroke-opacity', 0);
    legendItemsEnter
      .append('text')
      .text((d) => d.label)
      .attr('fill', this.textColor)
      .attr('x', LEGEND_TEXT_MARGIN)
      .attr('dy', '0.35em')
      .call(wordWrap, {
        maxWidth: LEGEND_TEXT_WIDTH,
        maxHeight: virtualTextBoxHeight,
        fontSize: NORMAL_FONT_SIZE,
      });
    // Update
    legendItems
      .merge(legendItemsEnter)
      .on('mouseover', (_event, d) => this.setHoverId(d.id))
      .on('mouseout', () => this.setHoverId(null));
    legendItems
      .select<SVGCircleElement>('circle')
      .merge(legendItemsCircle)
      .attr('stroke-opacity', (d) =>
        this.hoverId !== null && this.hoverId === d.id ? 1 : 0,
      );
    // Exit
    legendItems.exit().remove();
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns resolved promise
   */
  public update = (): Promise<void> => {
    this.updateAxes();
    this.updateLines();
    this.updateLegend();

    return Promise.resolve();
  };
}
